import { Link } from 'react-router-dom';
import { BookOpen, Calculator, FlaskConical, Globe, type LucideIcon } from 'lucide-react';
import { Badge } from './badge';
import { GED_SUBJECTS } from '../../data/siteContent';

const SUBJECT_VISUALS: Record<
  string,
  { icon: LucideIcon; image: string; span: string }
> = {
  math: {
    icon: Calculator,
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=1200&q=80',
    span: 'lg:col-span-2',
  },
  science: {
    icon: FlaskConical,
    image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=900&q=80',
    span: '',
  },
  'language-arts': {
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-14565130808-af50477667d7?auto=format&fit=crop&w=900&q=80',
    span: '',
  },
  'social-studies': {
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?auto=format&fit=crop&w=1200&q=80',
    span: 'lg:col-span-2',
  },
};

function Feature() {
  return (
    <section className="ged-overview w-full py-16 lg:py-24" aria-labelledby="what-is-ged-heading">
      <div className="mk-container mx-auto">
        <div className="flex flex-col gap-10">
          <div className="flex flex-col items-start gap-4">
            <Badge>GED basics</Badge>
            <div className="flex max-w-2xl flex-col gap-2">
              <h2
                id="what-is-ged-heading"
                className="text-left text-3xl font-bold tracking-tight md:text-4xl"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: '#0f172a' }}
              >
                What Is the GED?
              </h2>
              <p className="text-left text-base leading-relaxed text-muted-foreground md:text-lg">
                Four subjects. One U.S. high school equivalency credential.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {GED_SUBJECTS.map((subject: { id: string; slug: string; title: string; short: string; color: string }) => {
              const visual = SUBJECT_VISUALS[subject.id];
              if (!visual) return null;
              const Icon = visual.icon;
              const wide = Boolean(visual.span);
              return (
                <Link
                  key={subject.id}
                  to={`/program/${subject.slug}`}
                  className={[
                    'group relative flex min-h-[240px] flex-col justify-between overflow-hidden rounded-md p-6 text-white no-underline',
                    wide ? 'aspect-square sm:aspect-auto lg:col-span-2 lg:min-h-[280px]' : 'aspect-square',
                  ].join(' ')}
                  style={{ background: subject.color }}
                >
                  <img
                    src={visual.image}
                    alt=""
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-35 transition-opacity duration-300 group-hover:opacity-45"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
                  <Icon className="relative z-10 h-8 w-8 stroke-1" aria-hidden="true" />
                  <div className="relative z-10 flex flex-col">
                    <h3 className="text-xl font-semibold tracking-tight text-white">{subject.title}</h3>
                    <p className="mt-1 max-w-xs text-base text-white/85">{subject.short}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export { Feature };
